/**
 * Why the last session ended, so the login page can say so instead of just appearing.
 *
 * Kept in localStorage, not sessionStorage: a sign-out in one tab signs every tab out, and each of them
 * lands on the login page. Cleared by the next successful sign-in, so a later manual sign-out never shows a
 * stale reason.
 */

/** Minutes without activity before the portal logs the user out. The warning appears a minute earlier. */
export const IDLE_TIMEOUT_MINUTES = 30;

const KEY = 'isp_signed_out';
/** Older than this, the note no longer describes what just happened to the person reading it. */
const NOTICE_TTL_MS = 12 * 60 * 60 * 1000;

/** `idle`: the inactivity timer ran out. `expired`: the session could not be renewed. */
export type SignOutReason = 'idle' | 'expired';

interface SignOutRecord {
  reason: SignOutReason;
  minutes?: number;
  at: number;
}

export function recordSignOut(reason: SignOutReason): void {
  // A sign-out elsewhere already said why; a renewal refused afterwards must not replace that.
  if (reason === 'expired' && readSignOutNotice()) return;
  try {
    const record: SignOutRecord = { reason, at: Date.now(), ...(reason === 'idle' ? { minutes: IDLE_TIMEOUT_MINUTES } : {}) };
    localStorage.setItem(KEY, JSON.stringify(record));
  } catch {
    // Storage unavailable: the login page simply shows no reason.
  }
}

/** The sentence for the login page, or null when the last sign-out needs no explanation. */
export function readSignOutNotice(): string | null {
  try {
    const record = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<SignOutRecord> | null;
    if (!record || typeof record.at !== 'number' || Date.now() - record.at > NOTICE_TTL_MS) return null;
    if (record.reason === 'idle') {
      const minutes = typeof record.minutes === 'number' ? record.minutes : IDLE_TIMEOUT_MINUTES;
      return `You were logged out after ${minutes} minutes of inactivity. Sign in to continue.`;
    }
    if (record.reason === 'expired') return 'Your session expired. Sign in again to continue.';
    return null;
  } catch {
    return null;
  }
}

export function clearSignOutNotice(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing to clear.
  }
}
